import React, { useState } from 'react'
import { useCookies } from 'react-cookie'
import { useRouter } from 'next/router'
import Analytics from 'utils/googleAnalytics'

import DeckService from 'services/deck.service'
import ToastService from 'services/toast.service'

import GlobalOverlayStack from 'components/elements/Overlay/GlobalOverlayStack'
import PhatInput from 'components/formElements/PhatInput'
import PhatDropdown from 'components/formElements/PhatDropdown'
import PhatButton from 'components/formElements/PhatButton'
import CheckboxFancybox from 'components/formElements/CheckboxFancybox'
import BracketDropdown from 'components/misc/BracketDropdown'
import SimpleSpinner from 'components/elements/SimpleSpinner'

import { CardType, DEPRICATED_FORMAT_IDS, FORMAT, FormatType, HUMAN_FORMAT } from 'types/deck'
import { DeckRequest } from 'services/apiTypes/deck.types'

import {
  newDeckFormat,
  newDeckGameType,
  newDeckPrivate,
  newDeckTheorycrafted,
  newDeckUnlisted,
  newDeckCommanderBracket,
} from 'services/accountSettings.service'
import { generateDeckUrl } from 'utils/deckSeo'

import styles from './createDeckWithCardsOverlay.module.scss'

const validateCachedFormat = (cachedFormat: any): FormatType => Number(cachedFormat) as FormatType
const validateCachedBoolean = (cachedBoolean: string | undefined | null): boolean => `${cachedBoolean}` === 'true'

type Props = {
  cards: CardType[]
  open: boolean
  onClose: () => void
}
const CreateDeckWithCardsOverlay = ({ cards, open, onClose }: Props) => {
  const router = useRouter()

  const [
    {
      [newDeckFormat]: cachedFormat = FORMAT.EDH,
      [newDeckPrivate]: cachedPrivate = false,
      [newDeckUnlisted]: cachedUnlisted = false,
      [newDeckTheorycrafted]: cachedTheorycrafted = false,
      [newDeckCommanderBracket]: cachedCommanderBracket = null,
    },
    setCookies,
  ] = useCookies([
    newDeckFormat,
    newDeckPrivate,
    newDeckUnlisted,
    newDeckTheorycrafted,
    newDeckGameType,
    newDeckCommanderBracket,
  ])

  const [loading, setLoading] = useState(false)
  const [name, setName] = useState('')
  const [deckFormat, setDeckFormat] = useState(validateCachedFormat(cachedFormat))
  const [deckBracket, setDeckBracket] = useState<any>(cachedCommanderBracket)
  const [privateDeck, setPrivateDeck] = useState(validateCachedBoolean(cachedPrivate))
  const [unlistedDeck, setUnlistedDeck] = useState(validateCachedBoolean(cachedUnlisted))
  const [theorycraftedDeck, setTheorycraftedDeck] = useState(validateCachedBoolean(cachedTheorycrafted))

  const handleNewDeckSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()

    if (!name) return ToastService.create('Deck name is required', 'Deck Creation Service')

    setLoading(true)

    const body: Partial<DeckRequest> = {
      name,
      deckFormat,
      edhBracket: deckFormat === FORMAT.EDH ? deckBracket : null,
      private: privateDeck,
      unlisted: unlistedDeck,
      theorycrafted: theorycraftedDeck,
      extras: {
        forceCardsToSingleton: false,
        ignoreCardsOutOfCommanderIdentity: true, // TODO - make this an option
      },
    }

    try {
      const { id } = await DeckService.create(body)
      DeckService.add(id, cards).catch(err =>
        ToastService.create(`Failed to add cards to deck: ${err}`, 'Deck Creation Service'),
      )

      Analytics.signalEvent(`Deck created: ${deckFormat}`)

      setCookies(newDeckFormat, deckFormat)
      if (deckFormat === FORMAT.EDH) setCookies(newDeckCommanderBracket, deckBracket)
      setCookies(newDeckPrivate, privateDeck)
      setCookies(newDeckUnlisted, unlistedDeck)
      setCookies(newDeckTheorycrafted, theorycraftedDeck)

      router.push(generateDeckUrl(id, name))
    } catch (err: any) {
      setLoading(false) // Only set loading to false if we've failed. Otherwise, just waiting until the page transitions

      let errorMessage = ''

      if (err?.name && typeof err.name === 'string') errorMessage += err.name
      if (err?.name && Array.isArray(err.name)) errorMessage += err.name.join(' ')
      if (err?.non_field_errors) errorMessage += err.non_field_errors.join(' ')

      if (errorMessage) ToastService.create(errorMessage, 'Deck Creation Service')
    }
  }

  return (
    <GlobalOverlayStack open={open} onClose={onClose} className={styles.container}>
      <h1>Create deck with ({cards.length}) cards</h1>
      <div className={styles.optionsContainer}>
        <PhatInput
          focusOnMount
          value={name}
          onChange={setName}
          className={styles.fullWidth}
          header="Deck name"
          placeholder="Super awesome deck name 2000"
        />

        <PhatDropdown
          header="Format"
          value={deckFormat}
          className={`${styles.fullWidth} ${styles.formatDropdown}`}
          triggerWrapperClassName={styles.fullWidth}
          options={Object.keys(HUMAN_FORMAT)
            .map(key => ({
              label: key,
              id: `${HUMAN_FORMAT[key]}`,
              onClick: () => setDeckFormat(HUMAN_FORMAT[key]),
            }))
            .filter(obj => !Object.keys(DEPRICATED_FORMAT_IDS).includes(obj.id))}
        />

        {deckFormat === FORMAT.EDH && (
          <BracketDropdown
            header="Commander Bracket"
            value={cachedCommanderBracket}
            className={styles.formatDropdown}
            onChange={value => setDeckBracket(value)}
          />
        )}

        <div className={styles.flags}>
          <CheckboxFancybox
            checked={privateDeck}
            header="Private"
            onClick={() => setPrivateDeck(!privateDeck)}
            description="The deck is only visible to you"
          />
          <CheckboxFancybox
            checked={unlistedDeck}
            header="Unlisted"
            onClick={() => setUnlistedDeck(!unlistedDeck)}
            description="The deck is viewable with a direct link, but not shown in lists like search, folders, or your user page (for other users)"
          />
          <CheckboxFancybox
            checked={theorycraftedDeck}
            header="Theorycrafted"
            onClick={() => setTheorycraftedDeck(!theorycraftedDeck)}
            description='The deck is not included by default in "In decks" view'
          />
        </div>

        <div className={styles.controls}>
          <PhatButton disabled={loading} type="submit" color="green" onClick={handleNewDeckSubmit}>
            {loading ? (
              <>
                <SimpleSpinner size="xSmall" />
                Creating deck!
              </>
            ) : (
              <>Create deck</>
            )}
          </PhatButton>
        </div>
      </div>
    </GlobalOverlayStack>
  )
}

export default CreateDeckWithCardsOverlay
